import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents } from "../../api/studentApi";
import { assignForthSubject, listForthSubjectAssignments, deleteForthSubjectAssignments } from "../../api/examForthSubjectAssignApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const subjectApi = createLookupApi("subjects");

const URL_TO_TAB = {
  "exam-forth-subject-assign": "assign",
  "exam-forth-subject-assigned-list": "list",
};

const TABS = [
  { key: "assign", label: "Assign (4th) Sub." },
  { key: "list", label: "Assign List (4th) Sub." },
];

/**
 * Direct port of digicampusbd's `exam-forth-subject-assign` /
 * `exam-forth-subject-assigned-list` (`ExamForthSubjectAssignController`/
 * `Service`) — assigns an optional/choosable "4th subject" (e.g. a science-
 * group student also taking a humanities elective) to specific students,
 * marked compulsory or optional exactly like the old app's own
 * `subjectStatus` (0/1) column. Genuinely new backend module (entity,
 * repository, service, controller) — nothing pre-existing to reuse, unlike
 * most of the last few reports in this category. This report was flagged
 * as a named prerequisite for the still-deferred
 * `exam-report-subject-wise-grading-summary`.
 */
export default function ExamForthSubjectAssignPage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "assign");

  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [subjects, setSubjects] = useState([]);

  const [students, setStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [groupIdFilter, setGroupIdFilter] = useState("");
  const [compulsorySubjectIds, setCompulsorySubjectIds] = useState([]);
  const [optionalSubjectIds, setOptionalSubjectIds] = useState([]);

  const [assignments, setAssignments] = useState([]);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setTab(URL_TO_TAB[pageUrl] || "assign");
  }, [pageUrl]);

  useEffect(() => {
    Promise.all([listClassConfigurations(), academicYearApi.list(), subjectApi.list()]).then(([cc, years, subs]) => {
      setClassConfigs(cc);
      if (cc.length > 0) setClassConfigurationId(String(cc[0].id));
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
      setSubjects(subs);
    });
  }, []);

  useEffect(() => {
    if (!classConfigurationId || !academicYear) return;
    // Guard against an in-flight request for a *previous* class/year selection
    // resolving after this one and clobbering state with stale (or empty) data
    // — the initial academic-year default fires this effect twice in quick
    // succession (once for the placeholder default, once for the real
    // selection), and without this guard the slower response can win.
    let cancelled = false;
    listStudents(academicYear, classConfigurationId).then((data) => {
      if (!cancelled) setStudents(data);
    });
    return () => {
      cancelled = true;
    };
  }, [classConfigurationId, academicYear]);

  const groups = useMemo(() => {
    const seen = new Map();
    students.forEach((s) => {
      if (s.groupId != null && !seen.has(s.groupId)) seen.set(s.groupId, { id: s.groupId, name: s.groupName });
    });
    return Array.from(seen.values());
  }, [students]);

  // Re-validate (not just "is something selected") whenever the class/year
  // changes the available groups — a previously-selected group id can still
  // be a non-empty string that happens not to exist in the *new* class's
  // group list, which a bare `!groupIdFilter` truthiness check would miss
  // (found live: switching class then academic year left a stale group id
  // selected with zero real matches, silently showing "no students found").
  useEffect(() => {
    if (groups.length === 0) return;
    if (!groups.some((g) => String(g.id) === groupIdFilter)) {
      setGroupIdFilter(String(groups[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const studentsInGroup = students.filter((s) => String(s.groupId) === groupIdFilter);

  const loadAssignments = () => {
    if (!classConfigurationId || !academicYear || !groupIdFilter) return;
    setLoading(true);
    setError("");
    listForthSubjectAssignments(classConfigurationId, academicYear, groupIdFilter)
      .then(setAssignments)
      .catch(() => setError("Failed to load assigned list."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (tab === "list") loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, classConfigurationId, academicYear, groupIdFilter]);

  const toggleStudent = (identificationId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(identificationId) ? prev.filter((id) => id !== identificationId) : [...prev, identificationId]
    );
  };

  const handleAssign = async () => {
    setError("");
    setSuccess("");
    if (selectedStudentIds.length === 0) {
      setError("Please select at least one student.");
      return;
    }
    if (compulsorySubjectIds.length === 0 && optionalSubjectIds.length === 0) {
      setError("Please select at least one compulsory or optional subject.");
      return;
    }
    setLoading(true);
    try {
      await assignForthSubject({
        identificationIds: selectedStudentIds,
        compulsorySubjectIds: compulsorySubjectIds.map(Number),
        optionalSubjectIds: optionalSubjectIds.map(Number),
      });
      setSuccess("4th subject assigned successfully.");
      setSelectedStudentIds([]);
      setCompulsorySubjectIds([]);
      setOptionalSubjectIds([]);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to assign 4th subject.");
    } finally {
      setLoading(false);
    }
  };

  const toggleAssignmentRow = (id) => {
    setSelectedAssignmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleDelete = async () => {
    if (selectedAssignmentIds.length === 0) return;
    setError("");
    setSuccess("");
    try {
      await deleteForthSubjectAssignments(selectedAssignmentIds);
      setSuccess("Assignment(s) deleted.");
      setSelectedAssignmentIds([]);
      loadAssignments();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const assignmentColumns = [
    { key: "select", label: "", render: (row) => <input type="checkbox" checked={selectedAssignmentIds.includes(row.id)} onChange={() => toggleAssignmentRow(row.id)} /> },
    { key: "customStudentId", label: "Student ID" },
    { key: "studentName", label: "Name" },
    { key: "roll", label: "Roll" },
    { key: "groupName", label: "Group" },
    { key: "subjectName", label: "Subject" },
    { key: "subjectStatus", label: "Status" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{tab === "assign" ? "Assign 4th Subject" : "4th Subject Assigned List"}</h1>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupIdFilter} onChange={(e) => setGroupIdFilter(e.target.value)}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {tab === "assign" ? (
        <>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Roll</th>
                  <th>Student ID</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {studentsInGroup.map((s) => (
                  <tr key={s.identificationId}>
                    <td>
                      <input type="checkbox" checked={selectedStudentIds.includes(s.identificationId)} onChange={() => toggleStudent(s.identificationId)} />
                    </td>
                    <td>{s.studentRoll}</td>
                    <td>{s.customStudentId}</td>
                    <td>{s.studentName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {studentsInGroup.length === 0 && <p>No students found for this class/group.</p>}

          <div className="attendance-filters" style={{ marginTop: "1rem" }}>
            <label>
              Compulsory Subject(s)
              <select multiple value={compulsorySubjectIds} onChange={(e) => setCompulsorySubjectIds(Array.from(e.target.selectedOptions, (o) => o.value))}>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Optional Subject(s)
              <select multiple value={optionalSubjectIds} onChange={(e) => setOptionalSubjectIds(Array.from(e.target.selectedOptions, (o) => o.value))}>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleAssign} disabled={loading}>
              {loading ? "Assigning..." : "Assign"}
            </button>
          </div>
        </>
      ) : (
        <>
          {loading ? <p>Loading...</p> : <DataTable columns={assignmentColumns} rows={assignments} />}
          {!loading && assignments.length === 0 && <p>No 4th-subject assignments found for this selection.</p>}
          <div className="form-actions">
            <button type="button" className="btn-danger" onClick={handleDelete} disabled={selectedAssignmentIds.length === 0}>
              Delete Selected
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
